// Which helper tools a league shows (RULES_SCHEMA §15c,
// PLAN-2026-10-29-helper-tools-switch).
//
// The switches as stored are what the commissioner set. The switches IN FORCE
// are what this file says: the trade read-out and the lineup total are built
// from our projected points, so with projections off they are off too, and
// their own switches wait, as set, for projections to come back. Every reader
// of the switches reads them through here, so that rule has one copy.

import { TOOL_DEFAULTS, type LeagueSettings, type ToolSettings } from '../rules/schemas';

/** The switches in force, as every league view's `LeagueRef.tools` carries them. */
export type ToolsRef = ToolSettings;

/** Every helper on: the default, and what a league whose rules cannot be read shows. */
export const ALL_TOOLS: ToolsRef = Object.freeze({ ...TOOL_DEFAULTS });

export function effectiveTools(tools: ToolSettings): ToolsRef {
  return {
    projections: tools.projections,
    suggestions: tools.suggestions,
    tradeAnalysis: tools.projections && tools.tradeAnalysis,
    sandbox: tools.projections && tools.sandbox,
  };
}

export const toolsRefOf = (settings: Pick<LeagueSettings, 'tools'>): ToolsRef =>
  effectiveTools(settings.tools);

/**
 * The switches from a stored settings blob, read for the ref without parsing
 * the whole of the rules, as `formatRefOfStored` reads the format. Only an
 * explicit `false` turns a helper off: a version stored before the section
 * existed, and a blob that will not parse, show every helper, which is what
 * the parse's default says too.
 */
export function toolsRefOfStored(json: string | null): ToolsRef {
  if (json === null) return ALL_TOOLS;
  try {
    const t = (JSON.parse(json) as { tools?: Record<string, unknown> | null }).tools ?? {};
    return effectiveTools({
      projections: t.projections !== false,
      suggestions: t.suggestions !== false,
      tradeAnalysis: t.tradeAnalysis !== false,
      sandbox: t.sandbox !== false,
    });
  } catch {
    return ALL_TOOLS;
  }
}
